﻿import React, { useState, useEffect } from 'react';
import { Cpu, RefreshCw, Wrench, Sparkles, Search, Zap, Brain, Globe, Bot, Layers, Settings2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { socketService } from '@/services/socketService';
import { apiFetch } from '../services/apiClient';
import { MCPConfigDialog } from './MCPConfigDialog';
import { getConfigSchema, hasRequiredFields, isConfigComplete } from '../lib/mcpConfigSchemas';

interface MCPServer {
  name: string;
  label?: string;
  command: string;
  args: string[];
  enabled: boolean;
  connected: boolean;
  source?: 'local' | 'external' | 'builtin';
  autoGenerated?: boolean;
  toolCount?: number;
  description?: string;
  icon?: any;
  category?: 'business' | 'office' | 'iot' | 'rpa' | 'collaboration';
}

interface ServerHealth {
  status: 'connected' | 'restarting' | 'crashed' | 'failed' | 'disconnected';
  consecutiveCrashes: number;
  lastCrashTime?: string;
  lastSuccessfulConnect?: string;
}

type HealthMap = Record<string, ServerHealth>;

// 预定义的业务场景图标和分类
const BUILTIN_ADAPTERS = [
  { name: 'wechat_bot', label: 'WeChat 自动回复', icon: Globe, category: 'business', desc: '微信公众号 AI 自动回复机器人' },
  { name: 'enterprise_wechat_scrm', label: '企微 SCRM', icon: Zap, category: 'business', desc: '企业微信客户管理与 AI 客服' },
  { name: 'feishu_minutes', label: '飞书妙记', icon: Brain, category: 'office', desc: '飞书会议纪要生成与多维表格自动化' },
  { name: 'dingtalk_automation', label: '钉钉自动化', icon: Bot, category: 'office', desc: '钉钉审批流 + 群机器人 + IoT 联动' },
  { name: 'iot_gateway', label: 'IoT 网关编排', icon: Layers, category: 'iot', desc: '多协议设备统一管控与可视化编排' },
  { name: 'rpa_federation', label: 'RPA 联邦', icon: Cpu, category: 'rpa', desc: '跨平台浏览器与桌面自动化' },
  { name: 'agent_collaboration', label: 'Agent 协同', icon: Brain, category: 'collaboration', desc: '跨平台 Agent 协作与状态同步' },
];

// 分类标签中文映射
const CATEGORY_LABELS: Record<string, string> = {
  business: '业务场景',
  office: '办公效率',
  iot: '物联网',
  rpa: '流程自动化',
  collaboration: 'Agent 协同',
};

// 来源标签（替代 command/args 路径展示，避免泄露敏感路径）
const SOURCE_LABELS: Record<string, string> = {
  builtin: '内置适配器',
  local: '本地技能',
  external: '外部 MCP 服务',
};

export function MCPSettings({ t }: { t?: any }) {
  const isZh = t?.langCode !== 'en';
  const [servers, setServers] = useState<MCPServer[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterCategory, setFilterCategory] = useState<string>('all');

  const fetchServers = async () => {
    try {
      // Bug 11: 添加 res.ok 检查
      const res = await apiFetch('/api/skills');
      if (!res.ok) throw new Error('Failed to fetch skills');
      const data = await res.json();
      // Merge builtin adapters with fetched skills
      // ✅ Bug fix: 过滤掉可能被污染进 mcpConfig 的内置适配器条目（由内置适配器专区统一展示）
      const fetchedSkills = (data.skills || [])
        .filter((s: any) => !BUILTIN_ADAPTERS.some(ba => ba.name === s.name))
        .map((s: any) => ({
        name: s.name,
        command: 'npx',
        args: s.source === 'local' ? ['tsx', `~/xf_skills/${s.name}/index.ts`] : [],
        enabled: s.enabled,
        connected: s.connected,
        source: s.source,
        autoGenerated: s.autoGenerated,
        toolCount: s.toolCount,
        description: s.description,
        icon: null,
        category: s.category,
      }));

      // ✅ Bug fix: 内置适配器开关状态从 settings 读取（而非 `!!installed` —— 那会导致永远卡 ON）
      let builtinStates: Record<string, boolean> = {};
      try {
        const stRes = await apiFetch('/api/settings/mcp_builtin_states');
        if (stRes.ok) {
          const val = await stRes.json();
          if (val && typeof val === 'object') builtinStates = val;
        }
      } catch { /* 设置不存在时全部默认关闭 */ }

      // Add builtin adapters that are not yet installed
      const builtinAsServers = BUILTIN_ADAPTERS.map(ba => {
        const installed = fetchedSkills.find(s => s.name === ba.name);
        return {
          ...ba,
          command: 'node',
          args: [`./server/mcp/adapters/${ba.name}.ts`],
          enabled: builtinStates[ba.name] ?? false,
          connected: !!installed?.connected,
          source: 'builtin' as const,
          autoGenerated: true,
          toolCount: installed?.toolCount || 0,
          description: ba.desc,
        };
      });

      const mapped = [...builtinAsServers, ...fetchedSkills];
      setServers(mapped);
    } catch {
      // Fallback to traditional MCP endpoint
      try {
        const res = await apiFetch('/api/mcp');
        if (!res.ok) throw new Error('Failed to fetch MCP servers');
        const data = await res.json();
        setServers(data.servers || []);
      } catch {
        // MCP endpoint unavailable
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchServers(); }, []);

  const [healthMap, setHealthMap] = useState<HealthMap>({});

  // ── 统一配置框架状态 ──
  const [configServer, setConfigServer] = useState<MCPServer | null>(null);
  const [adapterConfigs, setAdapterConfigs] = useState<Record<string, Record<string, string>>>({});
  const [pendingEnable, setPendingEnable] = useState<string | null>(null); // 配置保存后待开启的服务

  const fetchAdapterConfigs = async () => {
    try {
      const res = await apiFetch('/api/settings/mcp_adapter_config');
      if (res.ok) {
        const val = await res.json();
        if (val && typeof val === 'object') setAdapterConfigs(val);
      }
    } catch { /* 适配器配置不存在时为空 */ }
  };

  useEffect(() => { fetchAdapterConfigs(); }, []);

  const fetchHealth = async () => {
    try {
      const res = await apiFetch('/api/mcp/health');
      if (res.ok) {
        const data = await res.json();
        setHealthMap(data.servers || {});
      }
    } catch { /* health endpoint unavailable */ }
  };

  useEffect(() => { fetchHealth(); }, []);

  useEffect(() => {
    const s = socketService.getSocket();
    if (!s) return;
    const handler = (data: { servers: HealthMap }) => {
      setHealthMap(data.servers || {});
    };
    s.on('mcp:health_update', handler);
    return () => { s.off('mcp:health_update', handler); };
  }, []);

  // Filter servers by category
  const filteredServers = filterCategory === 'all' 
    ? servers 
    : servers.filter(s => s.category === filterCategory);

  const toggleServer = async (name: string, enabled: boolean) => {
    // 开关联动：内置适配器含必填配置且未配置完整时，先弹配置框，保存后再开启
    if (enabled) {
      const target = servers.find(s => s.name === name);
      if (target) {
        const schema = getConfigSchema(name, target.source);
        if (schema.storage === 'settings' && hasRequiredFields(schema) && !isConfigComplete(schema, adapterConfigs[name] || {})) {
          setPendingEnable(name);
          setConfigServer(target);
          return;
        }
      }
    }
    await doToggle(name, enabled);
  };

  const doToggle = async (name: string, enabled: boolean) => {
    const updated = servers.map(s => s.name === name ? { ...s, enabled } : s);
    setServers(updated);

    // ✅ Bug fix: 内置适配器不走 /api/mcp（会污染 mcpManager 配置），改走通用 settings API
    if (updated.find(s => s.name === name)?.source === 'builtin') {
      try {
        await apiFetch('/api/settings', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ key: 'mcp_builtin_states', value: updated.filter(s => s.source === 'builtin').reduce((acc, s) => ({ ...acc, [s.name]: s.enabled }), {}) }),
        });
        toast.success(`${t?.mcpUpdateSuccess || '内置适配器已更新'}`);
        fetchServers();
        return;
      } catch (err: any) {
        console.warn('[MCPSettings] Toggle builtin failed:', err);
      }
    }

    // 外部 MCP 服务器：走单服务器 toggle 端点（服务端 read-modify-write，保留真实 args，
    // 避免全量 POST 时把 /api/skills 不返回的 command/args 伪造成 [] 写回，毁掉真实配置）
    try {
      const res = await apiFetch(`/api/mcp/toggle/${encodeURIComponent(name)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled }),
      });
      // Bug 10: 添加 res.ok 检查
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success(enabled ? (t?.mcpEnabled || '已启用') : (t?.mcpDisabled || '已禁用'));
      fetchServers();
    } catch (err: any) {
      toast.error(`${t?.mcpUpdateFailed || 'MCP 更新失败'}: ${err.message}`);
      fetchServers();
    }
  };

  // ── 可用性扫描 ──
  const [testingServer, setTestingServer] = useState<string | null>(null);
  const [scanningAll, setScanningAll] = useState(false);

  // 单服务器连接测试：已连接→ping；未连接→尝试重连
  const testServer = async (name: string) => {
    setTestingServer(name);
    try {
      const res = await apiFetch(`/api/mcp/test/${encodeURIComponent(name)}`, { method: 'POST' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const result = await res.json();
      if (result.status === 'connected') {
        toast.success(`${name}: ${t?.mcpTestOk || '连接正常'} · ${result.toolCount ?? 0} ${t?.mcpToolsUnit || '个工具'} · ${result.latencyMs ?? 0}ms`);
      } else if (result.status === 'disabled') {
        toast.warning(`${name}: ${t?.mcpTestDisabled || '服务已禁用，请先开启开关'}`);
      } else {
        toast.error(`${name}: ${t?.mcpTestFailed || '测试失败'} — ${result.error || result.status}`);
      }
    } catch (err: any) {
      toast.error(`${name}: ${t?.mcpTestFailed || '测试失败'} — ${err.message}`);
    } finally {
      setTestingServer(null);
      fetchHealth();
    }
  };

  // 全部扫描：并发测试所有已启用的非内置服务器
  const scanAll = async () => {
    const targets = servers.filter(s => s.enabled && s.source !== 'builtin');
    if (targets.length === 0) {
      toast.warning(t?.mcpScanNone || '没有已启用的外部 MCP 服务可扫描');
      return;
    }
    setScanningAll(true);
    try {
      const results = await Promise.allSettled(
        targets.map(s => apiFetch(`/api/mcp/test/${encodeURIComponent(s.name)}`, { method: 'POST' }).then(r => r.json())),
      );
      const ok = results.filter(r => r.status === 'fulfilled' && (r.value as any)?.status === 'connected').length;
      toast.success(`${t?.mcpScanDone || '扫描完成'}: ${ok}/${targets.length} ${t?.mcpScanAvailable || '可用'}`);
    } catch {
      toast.error(t?.mcpScanFailed || '扫描失败，请稍后重试');
    } finally {
      setScanningAll(false);
      fetchHealth();
    }
  };

  if (loading) {
    return (
      <div className="space-y-8 animate-in fade-in duration-500">
        <div className="flex items-center gap-3">
          <Cpu className="text-celestial-saturn" />
          <h3 className="text-xl font-bold uppercase tracking-tighter text-white/90">{t?.mcpServers || 'MCP 服务器'}</h3>
        </div>
        <p className="text-white/40 text-sm">{t?.mcpScanning || '正在扫描 MCP 协议服务器...'}</p>
      </div>
    );
  }

  const categories = Array.from(new Set(servers.map(s => s.category).filter(Boolean))) as string[];

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Cpu className="text-celestial-saturn" />
          <h3 className="text-xl font-bold uppercase tracking-tighter text-white/90">{t?.mcpServers || 'MCP 服务器'}</h3>
        </div>

        {/* Scan All + Category Filter */}
        <div className="flex items-center gap-2">
          <Button
            onClick={scanAll}
            disabled={scanningAll}
            variant="outline"
            className="text-xs px-3 h-8 rounded-xl bg-white/5 border-white/10 flex items-center gap-1.5"
            title={t?.mcpScanAllTooltip || '测试所有已启用外部服务的连接'}
          >
            <RefreshCw size={12} className={scanningAll ? 'animate-spin' : ''} />
            {scanningAll ? (t?.mcpScanningAll || '扫描中...') : (t?.mcpScanAll || '全部扫描')}
          </Button>
          <Button
            onClick={() => setFilterCategory('all')}
            variant={filterCategory === 'all' ? 'default' : 'outline'}
            className={`text-xs px-3 h-8 rounded-xl ${
              filterCategory === 'all' ? 'bg-celestial-saturn text-black' : 'bg-white/5 border-white/10'
            }`}
          >
            {t?.mcpAll || '全部'}
          </Button>
          {categories.map(cat => (
            <Button
              key={cat}
              onClick={() => setFilterCategory(cat)}
              variant={filterCategory === cat ? 'default' : 'outline'}
              className={`text-xs px-3 h-8 rounded-xl ${
                filterCategory === cat ? 'bg-celestial-saturn text-black' : 'bg-white/5 border-white/10'
              }`}
            >
              {CATEGORY_LABELS[cat] || cat}
            </Button>
          ))}
        </div>
      </div>

      <p className="text-sm text-white/40 max-w-xl">
        {t?.mcpDescription || 'MCP（模型上下文协议）服务通过社区维护的工具扩展智能体能力，业务场景适配器已预置。'}
      </p>

      {filteredServers.length === 0 ? (
        <div className="p-10 bg-white/5 rounded-[2rem] border border-white/5 text-center">
          <Wrench size={32} className="text-white/45 mx-auto mb-4" />
          <p className="text-white/40 font-bold uppercase tracking-widest text-sm">{t?.mcpNoServers || '未配置 MCP 服务器'}</p>
          <p className="text-white/45 text-xs mt-2">{t?.mcpAddHint || '请从下方添加或启用内置适配器'}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filteredServers.map(server => (
            <div
              key={server.name}
              className="relative p-6 bg-gradient-to-br from-white/[0.08] to-white/[0.03] rounded-2xl border border-white/5 space-y-4 hover:border-white/20 transition-all hover:shadow-lg hover:shadow-celestial-saturn/5"
            >
              <div className="flex items-start gap-3">
                {(() => {
                  const Icon = server.icon || Cpu;
                  return <Icon className="text-celestial-saturn flex-shrink-0 mt-0.5" size={24} />;
                })()}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="font-bold text-white text-sm truncate">{server.label || server.name}</h4>
                    {server.autoGenerated && (
                      <span className="text-[10px] px-1.5 py-0.5 bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 rounded-full font-bold uppercase flex items-center gap-0.5 flex-shrink-0">
                        <Sparkles size={6} /> {isZh ? '内置' : 'Builtin'}
                      </span>
                    )}
                    {server.category && (
                      <span className="text-[10px] px-1.5 py-0.5 bg-white/10 text-white/60 border border-white/10 rounded-full flex-shrink-0">
                        {CATEGORY_LABELS[server.category] || server.category}
                      </span>
                    )}
                  </div>
                  {server.label && (
                    <p className="text-[10px] text-white/30 font-mono truncate">{server.name}</p>
                  )}
                  <p className="text-xs text-white/55 line-clamp-2 mt-0.5">{server.description}</p>
                  {/* 不展示 command/args 等敏感路径，仅显示来源类型 */}
                  <p className="text-[11px] text-white/35 mt-1">{SOURCE_LABELS[server.source || 'external']}</p>
                </div>
                {/* 工具数徽章：标题行内联（修复 absolute 遮挡标题问题） */}
                {server.toolCount != null && (
                  <span className="flex-shrink-0 self-start text-xs font-bold bg-white/5 px-2 py-1 rounded-lg text-white/60">
                    {server.toolCount} {t?.mcpToolsUnit || '个工具'}
                  </span>
                )}
              </div>

              {/* Status Indicator */}
              <div className="flex items-center justify-between pt-3 border-t border-white/5">
                {(() => {
                  // 内置适配器非 mcpManager 子进程，无连接概念 —— 按开关状态显示
                  if (server.source === 'builtin') {
                    return (
                      <div className="flex items-center gap-2">
                        <div className={`w-1.5 h-1.5 rounded-full ${server.enabled ? 'bg-green-500 animate-pulse' : 'bg-white/20'}`} />
                        <span className={`text-xs font-bold ${server.enabled ? 'text-green-500' : 'text-white/45'}`}>
                          {server.enabled ? (t?.mcpEnabledState || '已启用') : (t?.mcpDisabledState || '已禁用')}
                        </span>
                      </div>
                    );
                  }

                  const h = healthMap[server.name];
                  const status = h?.status || (server.connected ? 'connected' : 'disconnected');
                  const dotColor =
                    status === 'connected' ? 'bg-green-500'
                    : status === 'restarting' ? 'bg-amber-400'
                    : status === 'crashed' || status === 'failed' ? 'bg-red-500'
                    : 'bg-white/20';

                  const dotAnim = status === 'connected' || status === 'restarting' ? 'animate-pulse' : '';

                  const statusLabel =
                    status === 'connected' ? (isZh ? '已连接' : 'Connected')
                    : status === 'restarting' ? (isZh ? '重启中' : 'Restarting')
                    : status === 'crashed' ? (isZh ? '已崩溃' : 'Crashed')
                    : status === 'failed' ? (isZh ? '已放弃' : 'Failed')
                    : (isZh ? '未连接' : 'Disconnected');

                  return (
                    <div className="flex items-center gap-2">
                      <div className={`w-1.5 h-1.5 rounded-full ${dotColor} ${dotAnim}`} />
                      <span className={`text-xs font-bold ${
                        status === 'connected' ? 'text-green-500'
                        : status === 'restarting' ? 'text-amber-400'
                        : status === 'crashed' || status === 'failed' ? 'text-red-400'
                        : 'text-white/45'
                      }`}>{statusLabel}</span>
                      {/* 单服务连接测试按钮 */}
                      <button
                        onClick={() => testServer(server.name)}
                        disabled={testingServer === server.name}
                        className="ml-1 p-1 rounded-lg hover:bg-white/10 text-white/40 hover:text-celestial-saturn transition-colors disabled:opacity-50"
                        title={t?.mcpTestConnection || '测试连接'}
                      >
                        <RefreshCw size={12} className={testingServer === server.name ? 'animate-spin' : ''} />
                      </button>
                    </div>
                  );
                })()}

                {/* 配置按钮 + Toggle Switch */}
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => setConfigServer(server)}
                    className="p-1.5 rounded-lg hover:bg-white/10 text-white/40 hover:text-celestial-saturn transition-colors"
                    title={t?.mcpConfigBtn || '配置'}
                  >
                    <Settings2 size={13} />
                  </button>
                  <button
                    onClick={() => toggleServer(server.name, !server.enabled)}
                    className={`relative w-12 h-6 rounded-full p-1 transition-colors cursor-pointer ${
                      server.enabled ? 'bg-celestial-saturn' : 'bg-white/10'
                    }`}
                  >
                    <div className={`w-4 h-4 rounded-full bg-white transition-transform ${
                      server.enabled ? 'translate-x-6' : 'translate-x-0'
                    }`} />
                  </button>
                </div>
              </div>

              {/* 健康指标：崩溃次数 + 最后连接时间（仅外部/本地服务） */}
              {server.source !== 'builtin' && (() => {
                const h = healthMap[server.name];
                if (!h) return null;
                const crashes = h.consecutiveCrashes || 0;
                const lastOk = h.lastSuccessfulConnect;
                if (crashes === 0 && !lastOk) return null;
                return (
                  <div className="flex items-center gap-3 text-[10px]">
                    {crashes > 0 && (
                      <span className="text-red-400">{t?.mcpCrashCount || '崩溃'} {crashes} {t?.mcpCrashUnit || '次'}</span>
                    )}
                    {lastOk && (
                      <span className="text-white/35">{t?.mcpLastConnect || '最后连接'}: {new Date(lastOk).toLocaleString(isZh ? 'zh-CN' : 'en-US')}</span>
                    )}
                  </div>
                );
              })()}

            </div>
          ))}
        </div>
      )}

      {/* GitHub Search Section */}
      <div className="p-6 glass-dark rounded-[2rem] border border-white/5 space-y-4">
        <div className="flex items-center gap-3">
          <Search className="text-celestial-saturn" size={18} />
          <h4 className="text-sm font-bold uppercase tracking-tight text-white">{t?.mcpSearchGitHubTitle || '在 GitHub 上搜索更多 MCP 服务'}</h4>
        </div>
        <div className="flex gap-3">
          <Input
            placeholder={t?.mcpSearchPlaceholder || '搜索 MCP 服务...'}
            className="flex-1 bg-white/5 border-white/10 rounded-xl py-2 text-sm"
          />
          <Button className="bg-white/5 hover:bg-white/10 border border-white/10 text-xs font-black uppercase tracking-widest px-4 h-10 rounded-xl">
            {t?.mcpSearchBtn || '搜索'}
          </Button>
        </div>
      </div>

      {/* Info Card */}
      <div className="p-6 glass-dark rounded-[2rem] border border-white/5 space-y-4">
        <div className="flex items-center gap-3">
          <Wrench className="text-celestial-saturn" size={18} />
          <h4 className="text-sm font-bold uppercase tracking-tight text-white">{t?.mcpInfoTitle || '说明'}</h4>
        </div>
        <p className="text-xs text-white/55 leading-relaxed">
          {t?.mcpInfoDesc || '业务场景适配器（企微 SCRM、飞书、钉钉、IoT、RPA、Agent 协同）已预置集成，打开开关即可启用对应能力。'}
        </p>
      </div>

      {/* Config Dialog */}
      {configServer && (
        <MCPConfigDialog
          open={!!configServer}
          onClose={() => setConfigServer(null)}
          server={configServer}
          onSaved={() => {
            fetchAdapterConfigs();
            if (pendingEnable) {
              const n = pendingEnable;
              setPendingEnable(null);
              setTimeout(() => doToggle(n, true), 300);
            }
          }}
          t={t}
        />
      )}
    </div>
  );
}
